import { PresenceState, UserPresenceStatus, UserPresenceView, UserSignIn } from "@/models/UserPresence";
import { formatDateAndTime } from "@/util/DateUtils";

/** The permission, held in addition to Admin/Users, that shows presence and sign-in history. */
export const VIEW_PRESENCE_PERMISSION = "Admin/Users/ViewPresence";

/** The Configuration field that turns presence tracking on or off for the tenant. */
export const PRESENCE_CONFIG_FIELD = "EnableUserPresenceTracking";

/**
 * How far before a session's first connection its sign-in row can be written. The app records the
 * sign-in as it loads, just before it connects; the server applies the same slack when it closes rows.
 */
export const SESSION_START_SLACK_MS = 2 * 60_000;

/** Idle users still hold a connection, so they count as connected (and pass "Online only"). */
export function isConnected(status: PresenceState | undefined): boolean {
    return status === "Online" || status === "Idle";
}

export function toPresenceView(status: UserPresenceStatus): UserPresenceView {
    const { connectedSince } = status;
    switch (status.status) {
        case "Online":
            return { status: "Online", connectedSince, tooltip: `Online since ${formatDateAndTime(connectedSince)}` };
        case "Idle":
            return { status: "Idle", connectedSince, tooltip: `Idle, last active ${formatDateAndTime(status.lastActivity)}` };
        default:
            return { status: "Offline", connectedSince, tooltip: `Last seen ${formatDateAndTime(status.lastSeen)}` };
    }
}

type LivePresence = Pick<UserPresenceView, "status" | "connectedSince">;

/**
 * Whether a sign-in row belongs to the user's live session. One session can own several rows, since
 * the app records a sign-in on every load once the sign-in throttle lapses, so this compares the row
 * with the session's start instead of assuming only the newest row is live.
 */
export function isInLiveSession(row: UserSignIn, presence: LivePresence | undefined): boolean {
    if (!presence || !isConnected(presence.status) || !presence.connectedSince) return false;
    return Date.parse(row.loginDate) >= Date.parse(presence.connectedSince) - SESSION_START_SLACK_MS;
}

/**
 * The sign-out cell of a sign-in history row. A missing sign-out is ambiguous: presence is held in
 * memory, so an API restart leaves rows that were never closed. Only a row from the session the user
 * is connected with right now can honestly read "Still signed in"; any other gap reads "Not recorded".
 */
export function signOutLabel(row: UserSignIn, presence: LivePresence | undefined): string {
    if (row.logoutDate) return formatDateAndTime(row.logoutDate);
    return isInLiveSession(row, presence) ? "Still signed in" : "Not recorded";
}
